<template>
  <loading-slice v-if="loading" />
  <div
    v-else-if="productDocument"
    v-waypoint="{
      active: activateV2025,
      callback: onWaypoint,
      options: intersectionOptions,
    }"
    class="product"
    :class="`product--${productDocument.status}`"
  >
    <purchase-sticky
      v-if="activateV2025"
      :product-document="productDocument"
      :shopify-product="shopifyProduct"
      :force-full="!productIsOnScreen"
      :product-ab-test="productAbTest"
    />
    <purchase-module
      v-else
      :product-document="productDocument"
      :shopify-product="shopifyProduct"
      :product-updated="productUpdated"
      :with-testimonials="
        productDocument.rating_average !== null &&
        productDocument.rating_number !== null
      "
      :product-ab-test="productAbTest"
    />

    <blocks
      v-if="activateV2025"
      :blocks="productDocument.blocks_v2025"
      :entities="{
        productDocument,
        selectedColor,
      }"
      :origin="ORIGIN.PRODUCT"
      class="product__blocks--v2025"
    />
    <blocks
      v-else
      :blocks="productDocument.blocks"
      :entities="{
        productDocument,
        selectedColor,
      }"
      :origin="ORIGIN.PRODUCT"
      nav
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import NuxtSSRScreenSize from 'nuxt-ssr-screen-size'
import slugify from 'slugify'
import ScrollTrackingMixin from '@/mixins/ScrollTrackingMixin'

import PurchaseModule from '@/slices/PurchaseModule/PurchaseModule'

import PurchaseSticky from '@/components/Purchase/PurchaseSticky'
import LoadingSlice from '@/components/loading/LoadingSlice'
import Blocks from '@/components/blocks/Blocks'

import services from '@/services'

import Product from '@/entities/shopify/Product'

import { ORIGIN } from '@/const'
import { EVENTS } from '@/const/events'
import { instianciateProduct } from '@/entities/sanity/product'

export default {
  name: 'Product',
  components: {
    LoadingSlice,
    PurchaseModule,
    PurchaseSticky,
    Blocks,
  },
  mixins: [NuxtSSRScreenSize.NuxtSSRScreenSizeMixin, ScrollTrackingMixin],
  async asyncData({
    params,
    route,
    $logError,
    i18n,
    error,
    payload,
    $prepareUidForContext,
  }) {
    try {
      let productDocument
      if (payload && payload.productDocument) {
        productDocument = instianciateProduct(payload.productDocument)
      } else {
        // Query to get post content
        const uid = $prepareUidForContext(params.uid)

        productDocument = await services.sanityService
          .getProduct({
            uid,
            lang: i18n.locale.slice(0, 2),
          })
          .then((res) => {
            if (res.length > 0) {
              return instianciateProduct(res[0])
            }

            return null
          })

        if (!productDocument) {
          throw new Error('product-not-found')
        }
      }

      let shopifyProductData
      if (payload && payload.shopify) {
        shopifyProductData = payload.shopify
      } else {
        const shopifyProductID = productDocument.shopify_product.id
        if (shopifyProductID) {
          shopifyProductData = await services.productService
            .getShopifyProduct(shopifyProductID)
            .then((result) => {
              if (result.data.errors) {
                throw new Error(result.data.errors[0].message)
              }

              if (!result.data.data.node) {
                throw new Error('product-not-found')
              }
              return result.data.data.node
            })
        }
      }

      let selectedColor =
        productDocument.colors && productDocument.colors[0]
          ? productDocument.colors[0]
          : null

      if (route.query.color && selectedColor) {
        const color = productDocument.colors.find(
          (c) => c._key === route.query.color
        )
        if (color) {
          selectedColor = color
        }
      }

      if (route.query.variant && shopifyProductData && selectedColor) {
        const variant = shopifyProductData.variants.edges.find((v) =>
          v.node.id.includes(route.query.variant)
        )

        if (variant) {
          let variantColor = variant.node.selectedOptions.find(
            (o) => o.name === 'Color'
          )
          if (variantColor) {
            variantColor = slugify(variantColor.value, {
              lower: true,
              strict: true,
            })
            if (productDocument.colors.some((c) => c._key === variantColor)) {
              selectedColor = productDocument.colors.find(
                (c) => c._key === variantColor
              )
            }
          }
        }
      }

      // Returns data to be used in template
      return {
        productDocument,
        selectedColor,
        shopifyProductData,
        loading: false,
      }
    } catch (e) {
      // Returns error page
      if (e.message !== 'product-not-found') {
        $logError.captureException(e)
      }
      error({ statusCode: 404, message: 'Page not found' })
    }
  },
  data() {
    return {
      loading: true,

      productDocument: null,
      selectedColor: null,

      shopifyProductData: null,
      shopifyProduct: null,
      inventoryLinksNumber: 0,

      activateV2025: false,
      productIsOnScreen: false,

      productUpdated: false,
      isSubscribed: false,
      timeout: null,
      ORIGIN,

      intersectionOptions: {
        root: null,
        rootMargin: '-50% 0px',
        threshold: [0, 1],
      },

      productAbTest: false,
    }
  },
  head() {
    if (this.productDocument) {
      const route = `${
        process.env.BASE_URL ? process.env.BASE_URL : 'https://www.asphalte.com'
      }${this.$nuxt.$route.path}`

      const head = services.pageService.getHead(
        {
          type: 'product',
          title: this.productDocument.title,
          route,
          defaultTitle: this.$t('seo.defaultTitle'),
          defaultDesc: this.$t('seo.defaultDesc'),
          sanity: this.productDocument,
          product: this.productDocument.shopify_product,
        },
        this.$generateAlternateLinksMetaSanity(
          this.productDocument._type,
          this.productDocument.alternate_languages
        )
      )

      const cc = ['header-transparent-mobile']

      if (this.activateV2025) {
        cc.push('product--v2025')
      }

      head.bodyAttrs = {
        class: cc.join(' '),
      }

      return head
    }

    return {}
  },

  computed: {
    ...mapGetters({
      authenticated: 'auth/authenticated',
      customer: 'auth/customer',
      account: 'subscriptions/account',
      connectedEmail: 'subscriptions/connectedEmail',
      isSubscriptionsLoading: 'subscriptions/isLoading',
      isSubscriptionsFetch: 'subscriptions/onFetch',
      campaigns: 'abtest/campaigns',
      isCampaignsLoading: 'abtest/isLoading',
    }),
  },
  watch: {
    isSubscriptionsLoading() {
      this.isSubscribed = this.account.isSubscribed(
        this.$prepareUidForContext(this.$route.params.uid)
      )
    },
    isSubscriptionsFetch() {
      this.isSubscribed = this.account.isSubscribed(
        this.$prepareUidForContext(this.$route.params.uid)
      )
    },
    isCampaignsLoading() {
      if (!this.isCampaignsLoading) {
        this.handleAlternativeCampaigns()
      }
    },
  },
  beforeDestroy() {
    this.$nuxt.$off(EVENTS.PRODUCT_COLOR_UPDATED)
    this.$nuxt.$off(EVENTS.USER_STATE_LOADED)

    this.deleteMorphoSelected(
      this.$prepareUidForContext(this.$route.params.uid)
    )
    this.setFixHeader(true)

    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  },
  created() {
    this.initProduct()

    if (process.client) {
      this.handleAlternativeCampaigns()
    }

    this.loading = false
  },
  async mounted() {
    this.$track.page(this.$metaInfo, this.$route, this.$nuxt.context.from)

    this.$nuxt.$on(EVENTS.PRODUCT_COLOR_UPDATED, (color) => {
      const productColor = this.productDocument.colors.find(
        (c) => c._key === color
      )
      if (productColor) {
        this.selectedColor = productColor
      }
    })

    if (
      this.productDocument.render_version === 'v2025' &&
      this.$route.query.version === 'v2025'
    ) {
      this.load2025Content().then((_) => {
        if (this.productDocument.blocks_v2025) {
          this.activateV2025 = true
        }
      })
    }

    this.setFixHeader(false)
    if (this.productDocument && this.shopifyProduct) {
      const result = await services.productService
        .refreshShopifyProduct(this.shopifyProduct.id, this.$i18n.locale)
        .then((r) => {
          if (r.status === 200) {
            return r.data.data.node
          }

          this.$logError.captureException(r)

          return null
        })

      if (result) {
        this.shopifyProduct.update(result)
        // await this.refreshAdditionnalInventory()
        this.$nuxt.$emit(EVENTS.PRODUCT_UPDATED)
        this.productUpdated = true
      }
    }

    if (
      this.connectedEmail === null ||
      (this.connectedEmail !== '' && this.account.email === null)
    ) {
      this.$nuxt.$on(EVENTS.USER_STATE_LOADED, (_) => {
        this.checkSubscription()
      })
    } else {
      this.checkSubscription()
    }

    this.initUserMoreThanOneMinutesOnPage()
  },
  methods: {
    ...mapMutations({
      deleteMorphoSelected: 'behavior/DELETE_MORPHO_SELECTED',
      setFixHeader: 'behavior/SET_FIX_HEADER',
    }),
    ...mapActions({
      activateTest: 'abtest/activate',
    }),
    initProduct() {
      if (this.productDocument) {
        this.productDocument = instianciateProduct(this.productDocument)
      }
      if (this.shopifyProductData) {
        this.shopifyProduct = new Product(this.shopifyProductData)
      }
    },

    async handleAlternativeCampaigns() {
      if (!this.isCampaignsLoading) {
        // Les campagnes sont déjà chargées
        // On lance le traitement
        const pp = []

        pp.push(this.handleAlternativeSliders())

        if (this.$vssWidth <= 769) {
          pp.push(this.handleAlternativeABTestRender())
        }

        await Promise.all(pp)
      }
    },

    async handleAlternativeABTestRender() {
      try {
        const uid = this.productDocument.uid

        if (this.campaigns.hasProductRenderTest(uid)) {
          const productCampaign = this.campaigns.getProductRenderTest(uid)

          if (productCampaign) {
            this.activateTest({
              campaignName: productCampaign.campaignName,
              variationId: productCampaign.variation.id,
              variationName: productCampaign.variation.name,
              variationGroupId: productCampaign.variationGroupId,
            })
            this.productAbTest = true

            if (productCampaign.version === 'v2025') {
              await this.load2025Content()

              if (this.productDocument.blocks_v2025) {
                this.activateV2025 = true
              }
            }
          }
        }
      } catch (e) {
        this.$logError.captureException(e)
      }
    },
    async load2025Content() {
      try {
        const blockV2025 = await services.sanityService.getProductContent(
          {
            uid: this.productDocument.uid,
            lang: this.$i18n.locale.slice(0, 2),
          },
          'v2025'
        )

        if (blockV2025?.blocks_v2025) {
          this.productDocument.blocks_v2025 = blockV2025.blocks_v2025
        }
      } catch (e) {
        this.$logError.captureException(e)
      }
    },

    async handleAlternativeSliders() {
      const uid = this.productDocument.uid

      if (this.campaigns.hasProductSliderTest(uid)) {
        const productCampaigns = this.campaigns.getProductSliderTest(uid)
        for (const campaign of productCampaigns) {
          if (!campaign.isOriginal) {
            await this.loadABTestSlider(
              campaign.color,
              campaign.alternateSliderId
            )
          }
        }
      }
    },
    async loadABTestSlider(color, sliderId) {
      try {
        const sliderDocument = await services.sanityService.getSliderById({
          id: sliderId,
        })

        if (sliderDocument) {
          const productColor = this.productDocument.colors.find(
            (c) => c.name === color
          )
          if (productColor) {
            productColor.slider = sliderDocument
          }
        }
      } catch (e) {
        this.$logError.captureException(e)
      }
    },
    checkSubscription() {
      if (this.connectedEmail !== '') {
        this.isSubscribed = this.account.isSubscribed(
          this.$prepareUidForContext(this.$route.params.uid)
        )
      }
    },
    initUserMoreThanOneMinutesOnPage() {
      if (this.timeout === null) {
        this.timeout = setTimeout(() => {
          this.hasSpendOneMinuteOnProductPage()
          clearTimeout(this.timeout)
          this.timeout = null
        }, 60000)
      }
    },
    hasSpendOneMinuteOnProductPage() {
      if (this.authenticated) {
        return services.laPosteService.hasSpendOneMinuteOnProductPage(
          this.customer.email,
          this.productDocument.uid,
          this.$i18n.locale.slice(0, 2)
        )
      }

      if (this.connectedEmail && this.connectedEmail !== '') {
        return services.laPosteService.hasSpendOneMinuteOnProductPage(
          this.connectedEmail,
          this.productDocument.uid,
          this.$i18n.locale.slice(0, 2)
        )
      }
    },

    onWaypoint(waypoint) {
      this.productIsOnScreen = waypoint.going === this.$waypointMap.GOING_IN
    },
  },
}
</script>

<style lang="scss">
.product--v2025 {
  padding-bottom: rem(260px);
}

.product__blocks--v2025 {
  .slice--video {
    padding: 0;
  }

  .slice--title {
    h2 {
      text-align: left;
    }

    + .block--impact-fairly {
      margin-top: calc(var(--spacing) * -2);
    }
  }
}
</style>
